import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import TemplateForm from './TemplateForm'
import TemplateSelector from './TemplateSelector'
import VariableInput from './VariableInput'
import EmailPreview from './EmailPreview'

export default function Dashboard() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [templates, setTemplates] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [variableValues, setVariableValues] = useState({})
  const [showTemplateForm, setShowTemplateForm] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [message, setMessage] = useState('')

  const loadTemplates = useCallback(async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*, template_categories!email_templates_category_id_fkey(id, name, color)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading templates:', error)
        return
      }

      if (data) {
        setTemplates(data)
      }
    } catch (err) {
      console.error('Unexpected error loading templates:', err)
    }
  }, [user])

  useEffect(() => {
    if (user) {
      loadTemplates()
    }
  }, [user, loadTemplates])

  const handleTemplateSelect = (template) => {
    console.log('Template selected:', template)
    console.log('Template name:', template.name)
    console.log('Template title:', template.title)
    console.log('Template content:', template.content)
    
    setSelectedTemplate(template)
    
    // Extract variables from template content
    const extractVariables = (content) => {
      const matches = content.match(/\{\{(\w+)\}\}/g)
      if (!matches) return []
      return [...new Set(matches.map(match => match.replace(/[{}]/g, '')))]
    }
    
    // Initialize variable values
    const initialValues = {}
    const detectedVariables = extractVariables(template.content || '')
    
    detectedVariables.forEach(variableName => {
      initialValues[variableName] = ''
    })
    
    console.log('Detected variables:', detectedVariables)
    console.log('Initial variable values:', initialValues)
    
    setVariableValues(initialValues)
  }

  const handleVariableChange = (name, value) => {
    setVariableValues(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleLogout = () => {
    signOut()
    navigate('/login')
  }

  const handleTemplateCreate = () => {
    setEditingTemplate(null)
    setShowTemplateForm(true)
  }

  const handleTemplateEdit = () => {
    setEditingTemplate(selectedTemplate)
    setShowTemplateForm(true)
  }

  const handleTemplateDelete = async () => {
    if (!selectedTemplate || !window.confirm('このテンプレートを削除しますか？')) return

    setDeleting(true)
    try {
      // Delete variables first (due to foreign key constraint)
      // Temporarily disabled due to variables table not being created yet
      console.log('Variables deletion disabled - table not created yet')
      // TODO: Re-enable when variables table is created in database
      /*
      const { error: variablesError } = await supabase
        .from('variables')
        .delete()
        .eq('template_id', selectedTemplate.id)

      if (variablesError) {
        console.error('Error deleting variables:', variablesError)
        // Continue with template deletion even if variables deletion fails
      }
      */

      // Delete template
      const { error } = await supabase
        .from('email_templates')
        .delete()
        .eq('id', selectedTemplate.id)

      if (error) {
        console.error('Delete error:', error)
        alert('削除中にエラーが発生しました: ' + error.message)
        return
      }

      loadTemplates()
      setSelectedTemplate(null)
      setMessage('テンプレートを削除しました')
      setTimeout(() => setMessage(''), 3000)
    } catch (err) {
      console.error('Unexpected error during delete:', err)
      alert('予期しないエラーが発生しました: ' + err.message)
    } finally {
      setDeleting(false)
    }
  }

  const handleTemplateSave = async (savedTemplate) => {
    try {
      setShowTemplateForm(false)
      
      // テンプレートリストを更新
      await loadTemplates()
      
      // 保存されたテンプレートを選択状態に設定
      if (savedTemplate) {
        setSelectedTemplate(savedTemplate)
      }
      
      setEditingTemplate(null)
      setMessage('テンプレートを保存しました')
      setTimeout(() => setMessage(''), 3000)
    } catch (error) {
      console.error('Error loading templates after save:', error)
      // エラーが発生してもフォームを閉じる
      setShowTemplateForm(false)
    }
  }


  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex flex-col">
      {/* Conditional Navigation - Hide when editing */}
      {!showTemplateForm && (
        <>
          <header className="bg-white/80 backdrop-blur-xl shadow-2xl border-b border-white/20 sticky top-0 z-40 transition-all duration-300">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="flex justify-between items-center py-3">
                <div className="flex items-center group">
                  <div className="relative">
                    <div className="absolute -inset-1.5 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full opacity-20 group-hover:opacity-30 transition-opacity duration-300 blur-sm"></div>
                    <img src="/favicon.png" alt="SalesHub" className="relative w-8 h-8 mr-3" />
                  </div>
                  <div>
                    <h1 className="text-xl font-bold bg-gradient-to-r from-gray-900 via-blue-900 to-indigo-900 bg-clip-text text-transparent">
                      SalesHub
                    </h1>
                    <p className="text-xs text-gray-500 font-medium">営業管理システム</p>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="group flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100/50 hover:bg-red-50 hover:text-red-600 rounded-lg transition-all duration-200 border border-gray-200/50 hover:border-red-200"
                >
                  <svg className="w-4 h-4 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  ログアウト
                </button>
              </div>
            </div>
          </header>

          <nav className="bg-white/40 backdrop-blur-sm border-b border-gray-200/30 transition-all duration-300">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="flex space-x-10">
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="relative text-sm font-semibold text-blue-600 py-4 group"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    メール管理
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"></div>
                </button>
                <button 
                  onClick={() => navigate('/templates')}
                  className="relative text-sm font-medium text-gray-600 hover:text-gray-900 py-4 group"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-gradient-to-r from-gray-100 to-gray-200 group-hover:from-indigo-100 group-hover:to-indigo-200 rounded-lg flex items-center justify-center shadow-sm group-hover:shadow-md transition-all">
                      <svg className="w-3 h-3 text-gray-600 group-hover:text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    テンプレート管理
                  </div>
                </button>
              </div>
            </div>
          </nav>
        </>
      )}

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-8 flex-1">
        {message && (
          <div className="mb-8 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200/50 rounded-2xl shadow-lg backdrop-blur-sm">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg mr-3">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-green-800 font-medium">{message}</span>
            </div>
          </div>
        )}
        
        {showTemplateForm ? (
          <TemplateForm
            user={user}
            template={editingTemplate}
            onSave={handleTemplateSave}
            onCancel={() => setShowTemplateForm(false)}
          />
        ) : (
          <>
            {!selectedTemplate ? (
              <div className="w-full max-w-6xl lg:w-[80vw] mx-auto">
                <TemplateSelector
                  templates={templates}
                  selectedTemplate={selectedTemplate}
                  onTemplateSelect={handleTemplateSelect}
                  onTemplateCreate={handleTemplateCreate}
                  onTemplateEdit={handleTemplateEdit}
                  onTemplateDelete={handleTemplateDelete}
                  deleting={deleting}
                />
              </div>
            ) : (
              <div className="w-full max-w-6xl lg:w-[80vw] mx-auto space-y-6">
                <TemplateSelector
                  templates={templates}
                  selectedTemplate={selectedTemplate}
                  onTemplateSelect={handleTemplateSelect}
                  onTemplateCreate={handleTemplateCreate}
                  onTemplateEdit={handleTemplateEdit}
                  onTemplateDelete={handleTemplateDelete}
                  deleting={deleting}
                />
                <div className="grid lg:grid-cols-5 gap-8 min-h-[600px]">
                  {/* Left Panel - Variables Input (Compact) */}
                  <div className="lg:col-span-2 space-y-4">
                    <VariableInput
                      template={selectedTemplate}
                      values={variableValues}
                      onChange={handleVariableChange}
                    />
                  </div>
                  {/* Right Panel - Email Preview (Expanded) */}
                  <div className="lg:col-span-3">
                    <EmailPreview
                      template={selectedTemplate}
                      variables={variableValues}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-6 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            {/* Left Section - Product Info */}
            <div className="flex items-center gap-3">
              <img src="/favicon.png" alt="SalesHub" className="w-5 h-5" />
              <div className="text-sm text-gray-500">
                <span className="font-medium text-gray-700">SalesHub</span> - 営業効率化ツール
              </div>
            </div>

            {/* Right Section - Developer Info */}
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>Developed by</span>
              <a 
                href="https://lunascent.net" 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-medium text-gray-700 hover:text-blue-600 transition-colors duration-200"
              >
                しゅん。
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}